import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContent, useWa, rich } from "@/lib/content";
import { EASE, SectionTitle, WhatsIcon } from "./Primitives";

const CORNERS = ["left-3 top-3 md:left-4 md:top-4", "right-3 top-3 -scale-x-100 md:right-4 md:top-4", "bottom-3 left-3 -scale-y-100 md:bottom-4 md:left-4", "bottom-3 right-3 rotate-180 md:bottom-4 md:right-4"];

// Marco con las esquinas del menú impreso
const Frame = () => (
  <>
    <span aria-hidden="true" className="pointer-events-none absolute inset-3 border border-gold/30 md:inset-4" />
    {CORNERS.map((c) => (
      <img key={c} src="/img/menu-art/esquina-dorada.png" alt="" aria-hidden="true" className={`pointer-events-none absolute h-9 w-auto opacity-80 md:h-12 ${c}`} />
    ))}
  </>
);

const Item = ({ it }) => (
  <li className="flex items-start gap-3 border-b border-gold/10 py-3">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold/50" />
    <div className="min-w-0">
      <p className="font-display text-lg leading-snug text-cream md:text-xl">{it.name}</p>
      {it.desc && <p className="mt-0.5 font-sans text-[13px] leading-relaxed text-sand">{it.desc}</p>}
    </div>
  </li>
);

export const MenuCompleto = () => {
  const { MENU_COMPLETO, TEXTS: T } = useContent();
  const MENU = Array.isArray(MENU_COMPLETO) ? MENU_COMPLETO : [];
  const waLink = useWa();
  const [active, setActive] = useState(MENU[0]?.key);
  const bar = useRef(null);
  const cat = MENU.find((c) => c.key === active) ?? MENU[0];

  useEffect(() => {
    const btn = bar.current?.querySelector('[data-active="true"]');
    if (btn && bar.current.scrollWidth > bar.current.clientWidth) bar.current.scrollTo({ left: btn.offsetLeft - 60, behavior: "smooth" });
  }, [active]);

  if (!cat) return null;

  return (
    <section id="menu" data-testid="menu-completo-section" className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28 lg:py-32">
      <SectionTitle eyebrow={T.mc_eyebrow} title={rich(T.mc_title)} de={T.mc_de} testid="menu-completo-title" />

      <div data-testid="menu-paper" className="relative mt-12 grid gap-8 rounded-[2rem] border border-gold/15 bg-roast/60 px-5 py-10 md:grid-cols-[220px_1fr] md:p-12 lg:grid-cols-[240px_1fr]">
        <Frame />
        <nav className="relative min-w-0">
          <div className="flex items-center gap-2 md:block">
            <button onClick={() => bar.current?.scrollBy({ left: -160, behavior: "smooth" })} aria-label="Anterior" data-testid="menu-completo-tabs-prev" className="shrink-0 rounded-full border border-gold/30 p-2 text-cream md:hidden">
              <ChevronLeft size={16} />
            </button>
            <div ref={bar} data-lenis-prevent data-testid="menu-completo-tabs" className="no-scrollbar flex flex-1 gap-2 overflow-x-auto md:sticky md:top-24 md:flex-col md:gap-0 md:overflow-visible">
              {MENU.map((c, i) => (
                <button
                  key={c.key}
                  data-testid={`menu-completo-tab-${c.key}`}
                  data-active={c.key === active}
                  onClick={() => setActive(c.key)}
                  className={`group flex shrink-0 items-center gap-3 rounded-full border px-4 py-2 text-left font-sans text-sm transition-colors md:rounded-none md:border-0 md:border-l md:px-4 md:py-2 ${
                    c.key === cat.key ? "border-gold bg-gold text-espresso md:bg-transparent md:text-gold" : "border-gold/20 text-sand hover:text-cream md:border-gold/10"
                  }`}
                >
                  <span className="hidden font-sans text-xs text-gold/60 md:inline">{String(i + 1).padStart(2, "0")}</span>
                  <span className={c.key === cat.key ? "md:font-display md:text-xl md:italic" : ""}>{c.title}</span>
                </button>
              ))}
            </div>
            <button onClick={() => bar.current?.scrollBy({ left: 160, behavior: "smooth" })} aria-label="Siguiente" data-testid="menu-completo-tabs-next" className="shrink-0 rounded-full border border-gold/30 p-2 text-cream md:hidden">
              <ChevronRight size={16} />
            </button>
          </div>
        </nav>

        <motion.div key={cat.key} initial={{ opacity: 0.2, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }} className="relative min-w-0">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-gold/15 pb-5">
            <div>
              <h3 data-testid="menu-completo-category-title" className="font-display text-3xl text-cream md:text-5xl">{cat.title}</h3>
              <p className="font-display text-lg italic text-gold">{cat.de}</p>
            </div>
            {cat.note && <p data-testid="menu-completo-note" className="max-w-sm font-sans text-xs leading-relaxed text-sand">{cat.note}</p>}
          </div>
          <div className={(cat.groups || []).length > 1 ? "gap-10 md:columns-2" : ""}>
            {(cat.groups || []).map((g, gi) => (
              <div key={`${g.title}-${gi}`} className="break-inside-avoid pb-6">
                <p className="mb-1 font-sans text-xs uppercase tracking-[0.3em] text-gold">{g.title}</p>
                <ul className={(cat.groups || []).length > 1 ? "" : "gap-10 md:columns-2 [&>li]:break-inside-avoid"}>
                  {(g.items || []).map((it, k) => (
                    <Item key={`${it.name}-${k}`} it={it} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-6 font-sans text-xs text-sand">Todos nuestros platillos pueden ser para llevar.</p>
        </motion.div>
      </div>
      <div className="mt-10 flex flex-wrap items-center gap-4">
        <a
          data-testid="menu-whatsapp-cta"
          href={waLink("wa_msg_menu")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-7 py-4 font-sans text-sm text-cream transition-colors hover:bg-gold hover:text-espresso"
        >
          <WhatsIcon className="h-4 w-4" /> {T.btn_menu_wa}
        </a>
      </div>
    </section>
  );
};
